/**
 * Pure formatters for `subscriptionMirrors` rows, shared by the cross-provider
 * console (#1701) and the admin user card (#1705).
 *
 * `formatRenewal` encodes a rule that is wrong by default, which is why there
 * is exactly one of it: `willRenew === null` means the provider does not expose
 * renewal intent — always so for supapass — and that is a different fact from
 * `false`. Rendering `null` as "No" shows a live subscription as expiring.
 * SPEC.md states this for the collection; this is where the display obeys it.
 *
 * Separate from `components/admin/subscriptionDisplay.tsx` only because that
 * file exports components, and mixing the two breaks fast refresh.
 */

import type { AdminSubscriptionInfo } from '../types/subscriptionMirror';

/** ISO 8601 → `YYYY-MM-DD HH:MM`, or an em dash. */
export function formatIso(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toISOString().slice(0, 16).replace('T', ' ');
}

/**
 * Tri-state renewal. `null` means the provider does not expose renewal intent,
 * which is NOT `false` — rendering it as "No" would show a live supapass
 * subscription as expiring.
 */
export function formatRenewal(row: AdminSubscriptionInfo): string {
  if (row.willRenew === null) return '—';
  if (row.willRenew) return 'Renews';
  return row.cancelAtPeriodEnd ? 'Ends at period end' : 'Will not renew';
}

/**
 * The same instant as `formatIso`, with the zone stated: `YYYY-MM-DD HH:MM UTC`.
 *
 * Use this wherever a timestamp stands on its own. `formatIso` stays bare for
 * table cells, where the column header carries the `(UTC)` instead of repeating
 * it on every row.
 *
 * Both are UTC because both go through `toISOString()`. That is worth stating
 * on screen because the OTHER date helper in this app, `utils/formatters.ts`'s
 * `formatDate`, is `toLocaleDateString` — browser-local. The admin user card
 * renders both, so an unlabelled timestamp there is genuinely ambiguous, and
 * labelling only one of the two would be worse than labelling neither: one
 * label licenses the assumption for both.
 */
export function formatUtc(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return `${parsed.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}
